import { type ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { parseISO } from "date-fns";
import { CircleSlash } from "lucide-react";
import { useTranslation } from "react-i18next";

import { DataTable, type DataTableFeatures } from "~/components/data-table";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui";
import type { OrganizationRow } from "~/features/admin/organizations/types";

interface OrganizationsTableProps {
  rows: OrganizationRow[];
  emptyMessage: string;
  globalFilter: string;
  onGlobalFilterChange: (next: string) => void;
  pageIndex: number;
  onPageIndexChange: (next: number) => void;
  pageSize: number;
  onPageSizeChange: (next: number) => void;
}

const helper = createColumnHelper<DataTableFeatures, OrganizationRow>();

export function OrganizationsTable({
  rows,
  emptyMessage,
  globalFilter,
  onGlobalFilterChange,
  pageIndex,
  onPageIndexChange,
  pageSize,
  onPageSizeChange,
}: OrganizationsTableProps) {
  const { t } = useTranslation();

  const columns: ColumnDef<DataTableFeatures, OrganizationRow, any>[] = [
    helper.display({
      id: "deactivatedAt",
      header: () => "",
      enableSorting: false,
      enableGlobalFilter: false,
      cell: (info) => {
        if (!info.row.original.isDeactivated) return null;
        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <CircleSlash className="h-4 w-4 text-warning" aria-label={t("ui.deactivated")} />
            </TooltipTrigger>
            <TooltipContent>{t("ui.deactivated")}</TooltipContent>
          </Tooltip>
        );
      },
    }),
    helper.accessor("name", {
      header: () => t("model.common.name"),
      cell: (info) => <span className="font-medium text-fg-1">{info.getValue()}</span>,
    }),
    helper.accessor("shortCode", {
      header: () => t("model.organization.shortCode"),
      cell: (info) => <span className="font-mono text-fg-2">{info.getValue()}</span>,
    }),
    helper.accessor("kind", {
      header: () => t("model.organization.kind"),
      cell: (info) => <span className="text-fg-2">{t(`model.organization.kindLabels.${info.getValue()}`)}</span>,
    }),
    helper.accessor("createdAt", {
      header: () => t("model.common.createdAt"),
      enableGlobalFilter: false,
      cell: (info) => (
        <span className="tabular-nums text-fg-2">{t("date.long", { date: parseISO(info.getValue()) })}</span>
      ),
    }),
  ];

  return (
    <DataTable<OrganizationRow>
      columns={columns}
      data={rows}
      emptyMessage={emptyMessage}
      globalFilter={globalFilter}
      onGlobalFilterChange={onGlobalFilterChange}
      pageIndex={pageIndex}
      onPageIndexChange={onPageIndexChange}
      pageSize={pageSize}
      onPageSizeChange={onPageSizeChange}
      getRowId={(row) => row.id}
    />
  );
}
